"use client";

import { cn } from "@/lib/utils";
import type { MapPlace } from "@/types/places";
import { useEffect, useRef } from "react";

interface CustomMapMarkerProps {
  place: MapPlace;
  isHovered?: boolean;
  isActive?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Number of places at this location. If > 1, shows a count badge. */
  clusterCount?: number;
  /** Accessible marker label resolved by the parent map component. */
  ariaLabel?: string;
}

/** English fallback for call sites that don't provide localized labels. */
function fallbackMarkerLabel(place: MapPlace, clusterCount: number): string {
  const labels =
    place.type === "event"
      ? { single: "Event", plural: "events" }
      : { single: "Club", plural: "clubs" };
  return clusterCount > 1
    ? `${clusterCount} ${labels.plural} at this location`
    : `${labels.single}: ${place.name}`;
}

export default function CustomMapMarker({
  place,
  isHovered = false,
  isActive = false,
  onMouseEnter,
  onMouseLeave,
  clusterCount = 1,
  ariaLabel,
}: CustomMapMarkerProps) {
  const isHighlighted = isHovered || isActive;
  const isClustered = clusterCount > 1;
  const markerRef = useRef<HTMLDivElement>(null);

  // Manage animation class outside React's className to prevent retriggering on re-renders
  useEffect(() => {
    const markerElement = markerRef.current;
    if (!markerElement) return;

    // Add animation class manually (once)
    markerElement.classList.add("map-marker-animated");

    const handleAnimationEnd = () => {
      // Remove after animation completes to prevent retriggering
      markerElement.classList.remove("map-marker-animated");
    };

    markerElement.addEventListener("animationend", handleAnimationEnd);
    return () => {
      markerElement.removeEventListener("animationend", handleAnimationEnd);
    };
  }, []); // Empty deps - run only once on mount

  const resolvedAriaLabel =
    ariaLabel ?? fallbackMarkerLabel(place, clusterCount);

  return (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: Google Maps advanced marker content, activation is handled by the map
    // biome-ignore lint/a11y/noStaticElementInteractions: hover handlers only, click is handled by the map
    <div
      ref={markerRef}
      aria-label={resolvedAriaLabel}
      className={cn(
        "relative cursor-pointer transition-all duration-200",
        // Enlarge mobile tap target without changing visual size
        "p-2 -m-2 pointer-events-auto touch-manipulation",
        isHighlighted ? "z-50" : "z-10",
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Pin SVG */}
      <svg
        width={isHighlighted ? 35 : 28}
        height={isHighlighted ? 40 : 32}
        viewBox="0 0 28 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn(
          "transition-all duration-200",
          isActive && "drop-shadow-lg",
        )}
        aria-hidden="true"
      >
        <path
          d="M14 0C6.268 0 0 6.268 0 14C0 21.732 14 32 14 32C14 32 28 21.732 28 14C28 6.268 21.732 0 14 0Z"
          fill={
            isHighlighted ? "var(--color-primary-light)" : "var(--foreground)"
          }
          className="transition-[fill] duration-200"
        />
        {/* Inner circle */}
        <circle
          cx="14"
          cy="14"
          r="6"
          fill="var(--background)"
          className="transition-all"
        />
      </svg>

      {/* Cluster count badge */}
      {isClustered && (
        <div
          className={cn(
            "absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5",
            "rounded-full bg-foreground text-background",
            "flex items-center justify-center",
            "shadow-md",
            "transition-all duration-200",
            // Ensure the badge doesn't intercept taps; allow clicks to fall through to marker
            "pointer-events-none",
            isHighlighted && "scale-110",
          )}
          aria-hidden="true"
        >
          <span className="text-xs font-medium leading-none">
            {clusterCount}
          </span>
        </div>
      )}
    </div>
  );
}
